import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { API_BASE_URL, buildApiUrl } from '@core/http/api.config';
import { Observable } from 'rxjs';
import { ImageData } from '@domains/image/model/api/image-data.interface';
import { ImageCategory } from '@domains/image/model/api/image-category.interface';
import { PagePagination } from '@shared/model/page-pagination.interface';
import { ImageDataNew } from '@domains/image/model/api/image-data-new.interface';
import { ImageDataUpdateRequest } from '@domains/image/model/api/image-data-update-request.interface';
import { DeleteImagesResponse } from '@domains/image/model/api/image-delete-response.interface';
import { GalleryContentType } from '@domains/image/model/gallery-content-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ImageApi {
  readonly #httpClient = inject(HttpClient);
  readonly #baseUrl = inject(API_BASE_URL);

  getListByWebsiteId(id: string): Observable<ImageData[]> {
    return this.#httpClient.get<ImageData[]>(buildApiUrl(this.#baseUrl, `/ContentImages/previews/${id}`));
  }

  imageList(
    pageNumber = 1,
    itemsPerPage = 999,
    categoryIds: number[] = [],
    searchValue = '',
    contentType = GalleryContentType.USER,
  ): Observable<PagePagination<ImageDataNew>> {
    let params = new HttpParams().set('PageNumber', pageNumber).set('ItemsPerPage', itemsPerPage);

    if (categoryIds.length > 0) {
      categoryIds.forEach(category => {
        params = params.append('CategoryIds', String(category));
      });
    }

    if (searchValue.length > 0) {
      params = params.append('Name', searchValue);
    }

    switch (contentType) {
      case GalleryContentType.USER:
        params = params.append('ShowPublic', String(false)).append('ShowOwned', String(true));
        break;
      case GalleryContentType.TESS:
        params = params.append('ShowPublic', String(true)).append('ShowOwned', String(false));
        break;
    }

    return this.#httpClient.get<PagePagination<ImageDataNew>>(buildApiUrl(this.#baseUrl, '/ContentImages/list'), {
      params,
    });
  }

  uploadImage(file: File, name?: string): Observable<ImageData> {
    const form = new FormData();

    form.append('file', file, file.name);

    if (name) {
      form.append('name', name);
    }

    return this.#httpClient.post<ImageData>(buildApiUrl(this.#baseUrl, '/ContentImages'), form);
  }

  uploadWithProgress(file: File, name?: string): Observable<HttpEvent<ImageDataNew[]>> {
    const form = new FormData();

    form.append('file', file, file.name);

    if (name) {
      form.append('name', name);
    }

    return this.#httpClient.post<ImageDataNew[]>(buildApiUrl(this.#baseUrl, '/ContentImages'), form, {
      observe: 'events',
      reportProgress: true,
    });
  }

  updateImagesInfoBatch(image: ImageDataUpdateRequest[]): Observable<ImageDataNew[]> {
    return this.#httpClient.put<ImageDataNew[]>(buildApiUrl(this.#baseUrl, '/ContentImages'), image);
  }

  categoryList(pageNumber = 1, itemsPerPage = 999): Observable<PagePagination<ImageCategory>> {
    const params = new HttpParams().set('PageNumber', pageNumber).set('ItemsPerPage', itemsPerPage);

    return this.#httpClient.get<PagePagination<ImageCategory>>(
      buildApiUrl(this.#baseUrl, '/ContentImages/categories'),
      { params },
    );
  }

  createCategory(categoryList: ImageCategory[]): Observable<ImageCategory[]> {
    return this.#httpClient.post<ImageCategory[]>(
      buildApiUrl(this.#baseUrl, '/ContentImages/categories'),
      categoryList,
    );
  }

  deleteImagesByIds(ids: number[]): Observable<DeleteImagesResponse> {
    let params = new HttpParams();

    if (ids?.length) {
      ids.forEach(id => {
        params = params.append('ids', String(id));
      });
    }

    return this.#httpClient.delete<DeleteImagesResponse>(buildApiUrl(this.#baseUrl, `/ContentImages`), { params });
  }
}
